import React, { useState } from 'react';
import { X, Youtube, ExternalLink } from 'lucide-react';
import mediaPlayerService from '../services/MediaPlayerService';

const MediaUrlModal = ({ isOpen, onClose, onSave, currentUrl = '' }) => {
  const [url, setUrl] = useState(currentUrl);
  const [isValid, setIsValid] = useState(!!currentUrl);
  const [error, setError] = useState('');

  const validateYouTubeUrl = (url) => {
    if (!url) return true; // Empty is valid (removes media)
    
    const videoId = mediaPlayerService.extractYouTubeId(url);
    return !!videoId;
  };

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    
    if (!newUrl) {
      setIsValid(true);
      setError('');
      return;
    }
    
    const valid = validateYouTubeUrl(newUrl);
    setIsValid(valid);
    setError(valid ? '' : 'Please enter a valid YouTube URL');
  };

  const handleSave = () => {
    if (isValid) {
      onSave(url.trim());
      onClose();
    }
  };

  const handleRemove = () => {
    onSave('');
    onClose();
  };

  const handlePreview = () => {
    if (url && isValid) {
      window.open(url, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-600" />
            <h2 className="text-lg font-semibold">Add YouTube Media</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="youtube-url" className="block text-sm font-medium text-gray-700 mb-2">
              YouTube URL
            </label>
            <input
              id="youtube-url"
              type="url"
              value={url}
              onChange={handleUrlChange}
              placeholder="https://www.youtube.com/watch?v=..."
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {error && (
              <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Supports youtube.com/watch, youtu.be, and youtube.com/embed URLs
            </p>
          </div>
          
          {url && isValid && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
              <Youtube className="w-4 h-4 text-red-600 flex-shrink-0" />
              <span className="text-sm text-gray-700 truncate flex-1">
                Video will play audio-only in the app
              </span>
              <button
                onClick={handlePreview}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                title="Preview on YouTube"
              >
                <ExternalLink className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          )}
        </div>
        
        <div className="flex gap-2 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cancel
          </button>
          {currentUrl && (
            <button
              onClick={handleRemove}
              className="px-4 py-2 text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
            >
              Remove
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!isValid}
            className={`flex-1 px-4 py-2 rounded-md transition-colors ${
              isValid
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaUrlModal;